import React, { Component } from 'react';
import axios from 'axios';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    // Log error to backend
    this.logErrorToServer(error, errorInfo);
  }

  logErrorToServer = async (error, errorInfo) => {
    try {
      // Get user info from localStorage if available
      const token = localStorage.getItem('token');
      let userId = null;
      let userRole = null;
      let restaurantId = null;

      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          userId = payload.user_id;
          userRole = payload.role;
          restaurantId = payload.restaurant_id;
        } catch (e) {
          // Token parsing failed, continue without user info
        }
      }

      // Get device info
      const deviceInfo = {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        online: navigator.onLine,
        cookieEnabled: navigator.cookieEnabled
      };

      // Prepare error data
      const errorData = {
        restaurant_id: restaurantId,
        user_id: userId,
        user_role: userRole,
        error_type: 'frontend',
        error_message: error?.message || error?.toString() || 'Unknown error',
        error_stack: error?.stack || null,
        component: errorInfo?.componentStack || null,
        url: window.location.href,
        user_agent: navigator.userAgent,
        device_info: deviceInfo
      };

      // Send to backend (note: endpoint is /api/log-error, not /api/admin/log-error)
      await axios.post(`${API_URL}/api/log-error`, errorData);
      
      console.log('Error logged successfully to server');
    } catch (logError) {
      // Silently fail - we don't want logging errors to cause more issues
      console.error('Failed to log error to server:', logError);
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-gray-800 rounded-2xl p-8 border border-gray-700 shadow-xl text-center">
            <div className="mb-6">
              <div className="w-20 h-20 mx-auto bg-red-500/20 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="w-10 h-10 text-red-500" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">
                Oops! Qualcosa è andato storto
              </h1>
              <p className="text-gray-400">
                Si è verificato un errore imprevisto. Il nostro team è stato notificato automaticamente.
              </p>
            </div>

            {/* Error details (only in development) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mb-6 text-left">
                <details className="bg-gray-900 rounded-lg p-4">
                  <summary className="text-red-400 cursor-pointer font-medium text-sm">
                    Dettagli errore (solo sviluppo)
                  </summary>
                  <pre className="mt-2 text-xs text-gray-400 overflow-auto max-h-40">
                    {this.state.error.toString()}
                    {this.state.errorInfo?.componentStack}
                  </pre>
                </details>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={this.handleReload}
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Ricarica Pagina
              </Button>
              <Button
                onClick={this.handleGoHome}
                variant="outline"
                className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                Torna alla Home
              </Button>
            </div>

            <p className="mt-6 text-xs text-gray-500">
              Se il problema persiste, contatta l'assistenza.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
